import { RobocloudEvents } from '@robocorp/robocloud-common/lib/types';
import {
  CronSchedule,
  IntervalSchedule,
  Schedule,
  WeekdaySchedule,
  ScheduleTargetType,
  ScheduleType,
} from '@robocorp/robocloud-scheduler-interface';
import { ProcessRun, WorkItemState } from '@robocorp/robocloud-orchestrator-interface';
import RcInstanceDTO from '@robocorp/robocloud-internal-cds-sdk/lib/sdk/Types';
import { Item } from './common';

/**
 * This structure states how many work items are currently in specific states
 */
export type WorkItemSummary = Record<WorkItemState, number | undefined>;

export type ProcessRunWorkItemSummaryWithoutOuputAndRegistered = Record<
  string,
  Omit<Record<WorkItemState, number>, 'REGISTERED' | 'OUTPUT'>
>;

export { WorkItemState };

export enum EnvGroupTokenExpiration {
  NEVER = 'NEVER',
  IN_A_DAY = 'IN_A_DAY',
  IN_A_WEEK = 'IN_A_WEEK',
  IN_A_MONTH = 'IN_A_MONTH',
  IN_A_YEAR = 'IN_A_YEAR',
}

export interface ProcessStatusWithRates extends ProcessStatus {
  itemSuccessRate: string;
  runSuccessRate: string;
}

export interface ProcessStatus {
  id: string; // Process id
  lastResult?: 'OK' | 'ERR';
  lastRunTs?: string;
  lastStartTs?: string;
  name: string;
}

// TODO: remove when Assistant SDK is out
export interface AssistantRobotConfig {
  id: string;
  task: string;
  name?: string;
}

// TODO: remove when Assistant SDK is out
export interface Assistant {
  name: string;
  id: string;
  createTs: string;
  robot: AssistantRobotConfig;
  lastRunTs: string;
  permissionConfiguration: {
    readSecrets: boolean;
    writeSecrets: boolean;
  };
  runCount: number;
  organizationId: string;
  workspaceId: string;
  active: boolean;
  createdBy: {
    userId: string;
    firstName: string;
    lastName: string;
    email: string;
    pictureUrl: string;
  };
  createdByApi: {
    apiKeyId: string;
    apiKeyName: string;
  };
}

export enum AssistantRunState {
  Complete = 'COMPL',
  InProgress = 'IP',
}

export enum AssistantRunResult {
  OK = 'OK',
  ERR = 'ERR',
}

export enum AssistantRunFullState {
  Complete = 'COMPLETE',
  InProgress = 'IN_PROGRESS',
}

export enum AssistantRunFullResult {
  Ok = 'OK',
  Error = 'ERROR',
  Terminated = 'TERMINATED',
  Timeout = 'TIMEOUT',
}

// TODO: remove when Assistant SDK is out
export interface AssistantRun {
  id: string;
  createTs: string;
  assistantId: string;
  assistantName: string;
  stateTs: string;
  startTs: string;
  robot: {
    task: string;
    id: string;
    name: string;
  };
  state: AssistantRunState;
  result: AssistantRunResult;
  duration: number;
  endTs: string;
  runNo: string;
  organizationId: string;
  workspaceId: string;
  startedBy: {
    userId: string;
    firstName: string;
    lastName: string;
    email: string;
    pictureUrl: string;
  };
}

export interface Secret {
  name: string;
  value?: Item[];
  description?: string;
}

export type ProcessScheduleTarget = Schedule['target'];

export { ScheduleType, ScheduleTargetType };

export type ProcessSchedule = Schedule;

export interface ProcessScheduleNextEvent {
  id: string;
  notBeforeTs: string;
}

export type IntervalProcessSchedule = IntervalSchedule;
export type CronProcessSchedule = CronSchedule;
export type WeekdaysProcessSchedule = WeekdaySchedule;

export type FileInfo = {
  fileName: string;
  fileSize: number;
  lastModified: string;
};

export type InstallerFile = {
  url: string;
  sha512: string;
  size: number;
};

export type InstallerInfo = {
  version: string;
  files: InstallerFile[];
  size?: number;
  path: string;
  sha512: string;
  releaseDate: string;
};

export type WorkspaceProcessRunsByState = {
  [state: string]: ProcessRun[];
};

export type EmailNotification = {
  isMemberOfWorkspace: boolean;
  email: string;
  party?: RcInstanceDTO.User;
  name?: string;
};

// TODO: move these to robocloud common
export type ActivityRunEvent = {
  localTime: string;
  seqNo: number;
  action: string;
  eventType: string;
  runId: string;
  ttl: number;
  runtimeId: string;
  workspaceId: string;
  data: any;
};

export type ActivityRunConsoleMessage = {
  localTime: string;
  seqNo: number;
  action: string;
  runId: string;
  category: string;
  message: string;
  ttl: number;
  runtimeId: string;
  workspaceId: string;
};

export type Artifact = {
  fileName: string;
  storageDetails: string;
  meta: {
    fileName: string;
    fileSize: number;
  };
  runId: string;
  id: string;
};

export type WorkItemFile = {
  id: string;
  name: string;
  size: number;
};

export type WorkItem = Omit<WorkItemDTO, 'files'> & {
  fileIds: string[];
  payload?: string;
  processRunId?: string;
  processRunNo?: string;
  queueTs?: string;
};

export type WorkItemPayload = any;

export type WorkItemDTO = {
  id: string;
  workspaceId: string;
  processId: string;
  processName: string;
  processRunId: string;
  runtimeId?: string;
  createTs: string;
  exception?: {
    message?: string;
    code?: string;
    type: 'BUSINESS' | 'APPLICATION' | 'ORCHESTRATOR' | 'UNSPECIFIED';
  };
  state: WorkItemState;
  stateTs: string;
  /**
   * Payload can be anything that serializes to JSON
   */
  payload?: WorkItemPayload;
  files?: WorkItemFile[];
  /**
   * @todo NOT IN USE BEFORE DP2.0
   */
  activityId?: string;
  /**
   * @todo NOT IN USE BEFORE DP2.0
   */
  activityRunId?: string;
  /**
   * @deprecated
   */
  created?: string;
  /**
   * @deprecated
   */
  workItemId: string;
  /**
   * @deprecated
   */
  status?: string;
  /**
   * @deprecated
   */
  storage?: string;
};

export type ActivityRunSocketInfo = {
  liveDataWebsocketEndpoint: string;
  liveDataWebsocketToken: string;
};

type WithConfirmUrl = { confirm_url: string };
export type WorkspaceInvitationWithConfirmUrl = RcInstanceDTO.WorkspaceInvitation & WithConfirmUrl;

type BySeqNoState = {
  [seqNo: number]: ActivityRunConsoleMessage;
};

export type ByActivityRunIdState = {
  [activityRunId: string]: { bySeqNo: BySeqNoState };
};

export type WorkspacesByOrganizationId = {
  orgId: string;
  workspaceIds: string[];
};

// TODO Billing: Use types from sdk
export interface LineItem {
  amount: number;
  currency: string;
  description: string;
}

export interface Invoice {
  id: string;
  number: string;
  totalAmount: number;
  currency: string;
  issuedDate: string;
  lineItems: Array<LineItem>;
  invoiceUrl?: string;
}

export type BillingDetails = {
  name: string;
  email: string;
  address: {
    line1: string;
    line2?: string;
    postalCode: string;
    city: string;
    state?: string;
    country: string;
  };
};

export type Subscription = {
  state: string;
  basePlanId: string;
  basePlan: BasePlan;
  changing?: boolean;
  changingAtTs?: string;
  successor?: {
    tier: {
      id: string;
    };
  };
  downgraded?: boolean;
};

export type BasePlan = {
  id: string;
  name: string;
  level: number;
  active: boolean;
  selfService: boolean;
  partnerPlan: boolean;
  paid: boolean;
  selectable: boolean;
  currency: 'USD';
  price: number;
  onDemandRunMinutePrice: number;
  includedActiveAssistants: number;
  extraActiveAssistantCost: number;
  extraActiveAssistantsAllowed: boolean;
  canTriggerProcessesWithEmail?: boolean;
  limits: {
    maxOnDemandRunMinutes: number | null;
    maxUsers?: number | null;
    maxWorkspaces?: number | null;
    maxOnDemandAssistantRuns: number | null;
  };
  onDemandAssistantRunPrice?: number;
};

export interface BillingInformation {
  name: string;
  email: string;
  address: {
    line1: string;
    line2?: string;
    city: string;
    postalCode: string;
    country: string;
    state?: string;
  };
  businessId?: string;
}

export type PaymentCard = {
  id: string;
  brand: string;
  country: string;
  expiryMonth: number;
  expiryYear: number;
  funding: 'credit';
  last4: string;
};

export type CardPaymentMethod = {
  type: 'card';
  card: PaymentCard;
  billingInformation?: BillingInformation;
  disabled: boolean;
};

export type PaymentMethod = CardPaymentMethod;

export interface SubscriptionUpdate {
  createdTs: string;
  previous: string;
  next: string;
}

export interface Usage {
  plan: string;
  quotaRunMinutesPerPeriod: number;
  /** total run minutes: quota + on demand */
  totalRunMinutes: number;
  usedQuotaRunMinutes: number;
  onDemandMinutes: number;
  onDemandMinutesLimit: number;
  selfHostedRunMinutes: number;
  selfHostedOnDemandMinutesCost: number;
  cloudRunMinutes: number;
  cloudOnDemandMinutesCost: number;
  allowOnDemandMinutes: boolean;
  planCost: number;
  onDemandMinutesCost: number;
  totalCost: number;
  assistantsCost: number;
  onDemandMinutesTotalCost: number;
  billingPeriodStartTs: string;
  billingPeriodEndTs: string;
  currency: string;
  updates: Array<SubscriptionUpdate>;
  limitOnDemandRunMinutes: boolean;
  unlimitedRunMinutesEnabled: boolean | null;
  /**
   * Assistant runs
   */
  allowOnDemandAssistantRuns: boolean;
  onDemandAssistantRuns: number;
  onDemandAssistantRunCost: number;
  onDemandAssistantRunsLimit: number;
  onDemandAssistantRunsTotalsCost: number;
  quotaAssistantRunsPerPeriod: number;
  totalAssistantRuns: number;
  usedQuotaAssistantRuns: number;
  limitOnDemandAssistantRuns: boolean;
}

export interface Limits {
  onDemandMinutesLimit?: number;
  allowOnDemandMinutes?: boolean;
  onDemandAssistantRunsLimit?: number;
  allowOnDemandAssistantRuns?: boolean;
  limitOnDemandRunMinutes?: boolean;
  limitOnDemandAssistantRuns?: boolean;
}

export interface RobotTask {
  robotId: string;
  taskName: string;
  robotName: string;
}

export interface WorkspaceKey {
  id: string;
  name: string;
  workspaceId: string;
  permissions: string[];
  lastUseTs?: string;
  createTs: string;
  uses: number;
  secret: string;
  createdBy: {
    firstName: string;
    lastName: string;
    picture: string;
  };
}

export type CloudEvent =
  | RobocloudEvents.ActivityCreatedEvent
  | RobocloudEvents.ActivityTemplateCreatedEvent
  | RobocloudEvents.ActivityTemplateDeletedEvent
  | RobocloudEvents.ActivityTemplateUpdatedEvent
  | RobocloudEvents.ProcessCreatedEvent
  | RobocloudEvents.UserCreatedEvent
  | RobocloudEvents.WorkspaceCreatedEvent
  | RobocloudEvents.WorkspaceInvitationAcceptedEvent
  | RobocloudEvents.WorkspaceInvitationCreatedEvent
  | RobocloudEvents.ProcessRunStartedEvent
  | RobocloudEvents.ProcessRunStoppingEvent
  | RobocloudEvents.ActivityRunStartedEvent
  | RobocloudEvents.ActivityRunUpdatedEvent
  | RobocloudEvents.WorkItemCompletedEvent
  | RobocloudEvents.UserLoggedInEvent
  | RobocloudEvents.RoboworkerDownloadedEvent
  | RobocloudEvents.WorkspaceDeletedEvent
  | RobocloudEvents.WorkspaceDataDeletedEvent
  | RobocloudEvents.SlackInviteRequestedEvent
  | RobocloudEvents.OrganizationCreatedEvent
  | RobocloudEvents.OrganizationLimitsBreachedEvent
  | RobocloudEvents.CloudProductOnboardingFinishedEvent
  | RobocloudEvents.WorkerCreatedEvent
  | RobocloudEvents.WorkerDeletedEvent
  | RobocloudEvents.AccessCredentialDeletedEvent
  | RobocloudEvents.AccessCredentialCreatedEvent
  | RobocloudEvents.AccessCredentialUsedEvent
  | RobocloudEvents.SubscriptionPlanChangedEvent
  | RobocloudEvents.SubscriptionCreatedEvent
  | RobocloudEvents.SubscriptionRenewalErrorEvent
  | RobocloudEvents.SubscriptionCanceledEvent
  | RobocloudEvents.SubscriptionRenewedEvent
  | RobocloudEvents.RunMinuteLimitHitEvent
  | RobocloudEvents.ResourceUsageResetEvent
  | RobocloudEvents.UserLoggedOutEvent
  | RobocloudEvents.OrganizationDeletedEvent
  | RobocloudEvents.OrganizationInviteSentEvent
  | RobocloudEvents.OrganizationInviteAcceptedEvent
  | RobocloudEvents.OrganizationMemberRemovedEvent
  | RobocloudEvents.ActivityRunCompletedEvent
  | RobocloudEvents.ProcessRunCompletedEvent
  | RobocloudEvents.ProcessRunDeletedEvent
  | RobocloudEvents.RunMinutesUsedEvent
  | RobocloudEvents.OrganizationUpdatedEvent
  | RobocloudEvents.ActivityTemplateFileUpdatedEvent
  | RobocloudEvents.AssistantCreatedEvent
  | RobocloudEvents.AssistantRunCompletedEvent
  | RobocloudEvents.VaultSecretCreatedEvent
  | RobocloudEvents.VaultSecretAccessedEvent
  | RobocloudEvents.WorkspaceKeyUsedEvent
  | RobocloudEvents.WorkspaceKeyCreatedEvent
  | RobocloudEvents.WorkspaceKeyDeletedEvent
  | RobocloudEvents.WorkspaceKeyUpdatedEvent
  | RobocloudEvents.WorkItemFileUploadedEvent
  | RobocloudEvents.WorkItemFileDownloadedEvent
  | RobocloudEvents.WorkItemDataReadEvent
  | RobocloudEvents.WorkItemDataWriteEvent
  | RobocloudEvents.WorkItemDeletedEvent
  | RobocloudEvents.OnDemandMinutesEnabledEvent
  | RobocloudEvents.OnDemandMinutesDisabledEvent
  | RobocloudEvents.RunMinuteUsageWarningEvent;

export interface GitInstallationInfo {
  name: string;
  createdAt?: string;
  active?: boolean;
  gitlabUrl?: string;
  gitlabClientId?: string;
}

export type GithubInstallation = {
  createdAt: string;
  installationId: number;
  organizationId: string;
  info: GitInstallationInfo;
};

export type GitlabInstallation = {
  createdAt: string;
  installationId: string;
  active?: boolean;
  info: GitInstallationInfo;
};

export type GithubRepo = {
  id: number;
  name: string;
  owner: string;
  gitUrl: string;
  defaultBranch: string;
  installationId: number;
};

export type RepositoryBranch = {
  name: string;
  isDefault: boolean;
};

export interface DeployInformation {
  robotId: string;
  repository: string;
  branch: string;
  ref?: string;
  updatedAt: string;
}

export interface BranchConfig {
  branch?: string;
  automaticDeployEnabled?: boolean;
  mode?: 'attached' | 'detached';
  gitRef?: string;
}

export enum OutgoingEventType {
  PROCESS_RUN = 'PROCESS_RUN',
  STEP_RUN = 'STEP_RUN',
  ASSISTANT_RUN = 'ASSISTANT_RUN',
  ROBOT_RUN_AGENT = 'ROBOT_RUN_AGENT',
}

export interface Webhook {
  id: string;
  name: string;
  endpoint: string;
  version: number;
  workspaceId: string;
  organizationId: string;
  processIds: string[];
  createTs: string;
  createdBy: WebhookCreatedBy;
  secret: string;
  eventsToSend?: OutgoingEventType[];
  paused?: boolean;
}

export type WebhookCreatedBy = {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  picture: string;
};

export interface SlackChannel {
  id: string;
  name: string;
}

export interface ProcessEmailTrigger {
  emailAddress: string;
  processId: string;
  createdAt: Date;
  workspaceId: string;
  organizationId: string;
  messageParsingEnabled: boolean;
  triggerProcess: boolean;
}

// Used in data explorer for shorter uri variables
export enum WorkItemStateShort {
  New = 'N',
  Pending = 'P',
  InProgress = 'I',
  Failed = 'F',
  Completed = 'C',
  Output = 'O',
}

export const workItemStateShortToLongMap = {
  [WorkItemStateShort.New]: WorkItemState.New,
  [WorkItemStateShort.Pending]: WorkItemState.Pending,
  [WorkItemStateShort.InProgress]: WorkItemState.InProgress,
  [WorkItemStateShort.Failed]: WorkItemState.Failed,
  [WorkItemStateShort.Completed]: WorkItemState.Completed,
  [WorkItemStateShort.Output]: WorkItemState.Output,
};

export type DataExplorerScope = 'process' | 'processRun';

export interface DataExplorerFilters {
  activityId?: string[];
  activityRunId?: string;
  state?: WorkItemStateShort[];
}

export type StepWithDisplayData = RcInstanceDTO.Activity & {
  displayName: string;
  displayEnvironment: string;
};

export type RobotSourceType = {
  installationId?: string | number;
  name: string;
  type: 'file' | 'cmdOnly' | 'git' | 'github' | 'gitlab';
  input: File | string | undefined;
  branch?: string;
  automaticDeployEnabled?: boolean;
};

// Duplicating type belonging to the search-sdk.
// This is due to issue when using sdks that have been updated to use AWS 3.x that will cause the UI build to fail
export interface SearchServiceAssistantRun {
  id: string;
  organizationId: string;
  workspaceId: string;
  assistantId: string;
  assistantName: string;
  duration?: number;
  createTs: string;
  startTs: string;
  endTs?: string;
  stateTs: string;
  result?: string;
  error?: string;
  runNo: number;
  state: string;
  startedBy: {
    userId: string;
    firstName: string;
    lastName: string;
    email: string;
    pictureUrl: string;
  };
  robot: {
    id: string;
    task: string;
    name: string;
  };
}

export interface StateUpdateInfo {
  currentRun: unknown;
  metrics?: {
    cpuUsage: {
      model: string;
      speed: number;
      times: {
        idle: number;
        irq: number;
        nice: number;
        sys: number;
        user: number;
      };
    }[];
    diskSpaceInfo: {
      available: number;
      fs: string;
      mount: string;
      size: number;
      type: string;
      use: number;
      used: number;
    }[];
    memoryUsage: {
      free: number;
      total: number;
    };
  };
  localTime: string;
  onlineSince: string | null;
  remoteIp: string | null;
  runtimeId: string;
  runtimeType: string;
  seqNo: number | null;
  state: 'BUSY' | 'IDLE' | 'OFFLINE' | 'RECONNECTING';
  timeStamp: string;
  ttl: number;
  workspaceId: string;
}

export interface RuntimeStateUpdate {
  localTime: string;
  onlineSince: string | null;
  remoteIp: string | null;
  runtimeId: string;
  runtimeType: string;
  seqNo: number | null;
  state: 'BUSY' | 'IDLE' | 'OFFLINE' | 'RECONNECTING';
  timeStamp: string;
  ttl: number;
  workspaceId: string;
  info: StateUpdateInfo;
}

export type VirtualRoomMeta = {
  realm: string;
  clientId: string;
  enableRefreshTokens: boolean;
  oidcServerDiscoveryURI: string;
  instanceId: 'eu1' | 'us1' | 'eu2' | 'ci';
};

/** ****************** METRICS **************** */
export interface DailyUsedRunMinutes {
  day: string;
  minutesUsed: number;
}

export interface ProcessRunMinuteMetrics {
  organizationId: string;
  workspaceId: string;
  processId: string;
  processName: string;
  dailyUsage: DailyUsedRunMinutes[];
}

export interface WorkspaceRunMinuteMetrics {
  workspaceId: string;
  workspaceName: string;
  processDailyUsage: ProcessRunMinuteMetrics[];
}

export interface OrganizationRunMinuteMetrics {
  organizationId: string;
  totalMinutes: number;
  workspaces: WorkspaceRunMinuteMetrics[];
}

export type StopRequirementsFromControlRoom = {
  // These differ from the Orchestrator-interface one that are more flexible
  // We only expose very specific requirements via the API at this point in time
  terminateOngoingActivityRuns: boolean;
  setRemainingWorkItemsAsDone: boolean;
  reason?: string;
};

export interface EventLog {
  id: string;
  type: string;
  name: string;
  description: string;
  level: string;
  detectedAt: string;
  runInfo?: unknown;
  data: unknown;
}

export interface StepRunInfo {
  activityName: string;
  processName: string;
  processId: string;
  pRunId: string;
  aRunId: string;
}

export type Paginated<T> = {
  data: T[];
  next?: string;
  has_more: boolean;
};

export type PaginatedEventLogs = Paginated<EventLog>;

export enum NotificationType {
  ERROR = 'ERROR',
  OFFLINE = 'OFFLINE',
}

export enum NotificationChannel {
  Email = 'Email',
}

export interface NotificationCondition {
  type: NotificationType;
}

export interface NotificationRule {
  sendTo: {
    type: NotificationChannel.Email;
    email: string;
  };
  conditions: NotificationCondition[];
}
export interface NotificationPreferences {
  workspaceId: string;
  rules: NotificationRule[];
}

export interface WorkerUpdate {
  workerId: string;
  name?: string;
  disabledState?: {
    reason: string | null;
  } | null;
}
